import { useState } from 'react';
import { FileText, Loader2, PlugZap, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminFetch } from '@/hooks/admin/useAdminApi';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';

/**
 * The Melt × SynergySuite EDI, by hand.
 *
 * 1. Test the connection — logs in to SynergySuite's SFTP and shows its folders.
 * 2. Price file for one store — preview it, then send it to the chosen folder.
 *
 * Backend: netlify/functions/admin-synergy-sftp.ts. Nothing here is scheduled.
 */

interface Entry { name: string; type: string; size: number }
interface TestResult { host: string; home: string; top: Entry[]; folders: { path: string; entries: Entry[] }[] }
interface PriceResult {
  sent: boolean;
  file_name: string;
  items: number;
  skipped_no_price: string[];
  content?: string;
  remote_path?: string;
}

export function SynergySftpCard() {
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);

  const [qbo, setQbo] = useState('1414');
  const [dir, setDir] = useState('');
  const [busy, setBusy] = useState<'preview' | 'send' | null>(null);
  const [preview, setPreview] = useState<PriceResult | null>(null);
  const [sent, setSent] = useState<PriceResult | null>(null);
  const [fileErr, setFileErr] = useState<string | null>(null);

  async function runTest() {
    setTesting(true);
    setTestErr(null);
    setTest(null);
    try {
      const r = await adminFetch<TestResult>('admin-synergy-sftp', { method: 'POST', body: JSON.stringify({ action: 'test' }) });
      setTest(r);
      if (!dir) setDir(r.home);
    } catch (e) {
      setTestErr(e instanceof Error ? e.message : 'error');
    } finally {
      setTesting(false);
    }
  }

  async function priceFile(send: boolean) {
    if (send && !window.confirm(`Send this price file to SynergySuite, into ${dir || '/'}?\n\nThis puts a real file on their server.`)) return;
    setBusy(send ? 'send' : 'preview');
    setFileErr(null);
    if (send) setSent(null); else setPreview(null);
    try {
      const r = await adminFetch<PriceResult>('admin-synergy-sftp', {
        method: 'POST',
        body: JSON.stringify({ action: 'price_file', qbo_customer_id: qbo.trim(), remote_dir: dir || '/', send }),
      });
      if (send) setSent(r); else setPreview(r);
    } catch (e) {
      setFileErr(e instanceof Error ? e.message : 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <CollapsibleCard
      title="The Melt — SynergySuite EDI"
      desc="The Melt is moving its purchase orders to SynergySuite, which swaps files with us over SFTP. Test the connection and send one store's price file here."
      storageKey="company-synergysuite-edi"
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-brix-ink">1. Test the connection</h3>
          <p className="text-xs text-brix-muted">Logs in to SynergySuite's server and lists its folders. It reads only; nothing is changed.</p>
          <Button variant="secondary" onClick={() => void runTest()} disabled={testing}>
            {testing ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />}
            {testing ? 'Connecting…' : 'Test SynergySuite connection'}
          </Button>
          {testErr && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{testErr}</div>}
          {test && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 space-y-2">
              <div>Connected to <b>{test.host}</b>. Starting folder: <code>{test.home}</code></div>
              {test.top.length === 0 && <div>The starting folder is empty.</div>}
              {test.top.length > 0 && (
                <ul className="text-xs space-y-1">
                  {test.top.map((e) => (
                    <li key={e.name}>
                      {e.type === 'd' ? '📁' : '📄'} {e.name}
                      {e.type === 'd' && (
                        <button type="button" className="ml-2 underline" onClick={() => setDir(`${test.home.replace(/\/+$/, '')}/${e.name}`)}>
                          send files here
                        </button>
                      )}
                      {e.type === 'd' && (() => {
                        const f = test.folders.find((x) => x.path.endsWith(`/${e.name}`));
                        if (!f || f.entries.length === 0) return null;
                        return <span className="text-emerald-700"> — {f.entries.map((x) => x.name).join(', ')}</span>;
                      })()}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-brix-ink">2. Price file for one store</h3>
          <p className="text-xs text-brix-muted">
            Lists only the items that store has ordered in the last 12 months, at its current prices. Preview it first; Send puts it on SynergySuite's server.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs text-brix-muted">
              Store's QuickBooks customer number
              <Input value={qbo} onChange={(e) => setQbo(e.target.value)} placeholder="1414 (Stanford)" />
            </label>
            <label className="text-xs text-brix-muted">
              Folder on SynergySuite's server
              <Input value={dir} onChange={(e) => setDir(e.target.value)} placeholder="Run the test to see folders" />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => void priceFile(false)} disabled={busy !== null || !qbo.trim()}>
              {busy === 'preview' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              Preview price file
            </Button>
            <Button onClick={() => void priceFile(true)} disabled={busy !== null || !qbo.trim() || !dir.trim()}>
              {busy === 'send' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send to SynergySuite
            </Button>
          </div>
          {fileErr && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{fileErr}</div>}
          {sent && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              Sent <b>{sent.file_name}</b> ({sent.items} items) to <code>{sent.remote_path}</code>. Ask Jeff to confirm it loaded.
            </div>
          )}
          {preview && (
            <div className="space-y-1">
              <div className="text-xs text-brix-muted">
                {preview.file_name} · {preview.items} items
                {preview.skipped_no_price.length > 0 && ` · left off (no price): ${preview.skipped_no_price.join(', ')}`}
              </div>
              <pre className="max-h-64 overflow-auto rounded-lg border border-brix-border bg-white p-2 text-[11px] leading-snug">{preview.content}</pre>
            </div>
          )}
        </div>
      </div>
    </CollapsibleCard>
  );
}
